import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses.js';
import { t } from '../../../../common/i18n';
import { getLocalizedString, getPriceFromType } from '../../../../common/utils/productOfferingsUtils.js';
import { PriceType } from '../../../HorizontalSubscriptionCard/types.js';
import {
  DELIVERY_GROUP_IDS,
  flattenDeliveryOfferings,
  formatDeliveryPrice,
} from '../../utils/deliveryMethodUtilsCoCo.js';
import type { DeliveryMethodGroup } from '../../../../generated/api/deliveryMethodGroup.js';
import type { DeliveryType } from '../../../../generated/api/deliveryType.js';
import type { ProductOfferingCoco } from '../../../../generated/api/productOfferingCoco.js';
import type { PlaceOrderFormData } from '../../placeOrderTypes.js';

import './DeliveryMethodList.scss';
import './DeliveryMethodListCoCo.scss';

interface DeliveryMethodListCoCoProps {
  deliveryMethodGroups?: DeliveryMethodGroup[];
  onClearError?: () => void;
}

// Icon mapping for the known delivery method group ids; falls back to `delivery`.
const GROUP_ICON_BY_ID: Record<string, string> = {
  [DELIVERY_GROUP_IDS.DIRECT]: 'delivery',
  [DELIVERY_GROUP_IDS.INDIRECT]: 'location',
};

export const DeliveryMethodListCoCo = ({ deliveryMethodGroups, onClearError }: DeliveryMethodListCoCoProps) => {
  const { setValue, watch } = useFormContext<PlaceOrderFormData>();
  const selectedDeliveryMethodId = watch('delivery.deliveryType');

  const availableGroups = (deliveryMethodGroups ?? []).filter(g => (g.deliveryProductOffering ?? []).length > 0);
  const allOfferings = flattenDeliveryOfferings(availableGroups);

  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (availableGroups.length === 0) {
      setSelectedGroupId(undefined);
      return;
    }
    const groupOfSelected = availableGroups.find(group =>
      group.deliveryProductOffering?.some(o => o.id === selectedDeliveryMethodId)
    );
    setSelectedGroupId(prev => prev ?? groupOfSelected?.id ?? availableGroups[0].id);
  }, [availableGroups, selectedDeliveryMethodId]);

  useEffect(() => {
    if (deliveryMethodGroups === undefined || !selectedDeliveryMethodId) {
      return;
    }

    const selectedMethodIsAvailable = allOfferings.some(method => method.id === selectedDeliveryMethodId);
    if (!selectedMethodIsAvailable) {
      setValue('delivery.deliveryType', undefined);
      setValue('delivery.deliveryPriceInCents', undefined);
    }
  }, [allOfferings, deliveryMethodGroups, selectedDeliveryMethodId, setValue]);

  // Auto-select when there is only one delivery method available across all groups.
  useEffect(() => {
    if (allOfferings.length === 1 && !selectedDeliveryMethodId) {
      const method = allOfferings[0];
      setValue('delivery.deliveryType', method.id as DeliveryType);
      setValue(
        'delivery.deliveryPriceInCents',
        getPriceFromType(method.productOfferingPrice, PriceType.ONE_TIME_CHARGE)
      );
      onClearError?.();
    }
  }, [allOfferings, selectedDeliveryMethodId, setValue, onClearError]);

  if (allOfferings.length === 0) {
    return null;
  }

  const currentGroup = availableGroups.find(g => g.id === selectedGroupId) ?? availableGroups[0];
  const offeringsForCurrentGroup = currentGroup?.deliveryProductOffering ?? [];

  const selectMethod = (method: ProductOfferingCoco) => {
    setValue('delivery.deliveryType', method.id as DeliveryType);
    setValue('delivery.deliveryPriceInCents', getPriceFromType(method.productOfferingPrice, PriceType.ONE_TIME_CHARGE));
    onClearError?.();
  };

  const onSelectGroup = (groupId: string) => {
    setSelectedGroupId(groupId);
    const stillValid = availableGroups
      .find(g => g.id === groupId)
      ?.deliveryProductOffering?.some(o => o.id === selectedDeliveryMethodId);
    if (!stillValid) {
      setValue('delivery.deliveryType', undefined);
      setValue('delivery.deliveryPriceInCents', undefined);
    }
  };

  return (
    <div className="of-place-order-delivery-method of-place-order-delivery-method-coco">
      <p className={dsClass.FONT_WEIGHT_BOLD}>{t.BTOF('Choose delivery method')}</p>
      <CL.Disclaimer
        className={dsClass.MARGIN_BOTTOM_4}
        disclaimerType="info"
        icon={<CL.Icon icon="information" />}
        text={t.LH4H(
          'Orders placed before 12 noon on weekdays will generally be delivered the next business day. The SIM card will be delivered in an envelope to the address selected.'
        )}
      />

      {availableGroups.length > 1 && (
        <CL.SelectGroup
          className={`of-place-order-delivery-method__modes ${dsClass.MARGIN_BOTTOM_4}`}
          products={availableGroups.map(group => ({
            id: group.id,
            name: getLocalizedString(group.name),
            icon: <CL.Icon icon={GROUP_ICON_BY_ID[group.id] ?? 'delivery'} color="black" type="light" />,
          }))}
          value={currentGroup?.id}
          onChange={onSelectGroup}
        />
      )}

      <div className="of-place-order-delivery-method__list">
        {offeringsForCurrentGroup.map(method => {
          const isSelected = selectedDeliveryMethodId === method.id;
          const priceInCents = getPriceFromType(method.productOfferingPrice, PriceType.ONE_TIME_CHARGE);
          const description = method.description ? getLocalizedString(method.description) : undefined;

          return (
            <div
              key={method.id}
              className={classNames('of-place-order-delivery-method__item', { selected: isSelected })}
            >
              <button
                type="button"
                className="of-place-order-delivery-method__item-button"
                onClick={() => selectMethod(method)}
              >
                <div className="of-place-order-delivery-method__item-content">
                  <div className="of-place-order-delivery-method__item-info">
                    <div className="of-place-order-delivery-method__item-header">
                      <strong>{method.name ? getLocalizedString(method.name) : ''}</strong>
                    </div>
                    {description && (
                      <div className="of-place-order-delivery-method__item-description">{description}</div>
                    )}
                  </div>
                  <div className="of-place-order-delivery-method__item-price">
                    <strong>{formatDeliveryPrice(priceInCents)}</strong>
                  </div>
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
